import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompose';
import { get, noop, orderBy } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import immutableToJS from 'helpers/immutableToJS';
import {
  QuickHelp,
  Tooltip,
} from 'components';
import { payOnlineHelper } from 'constants/strings';
import { refreshToken } from 'redux/auth/actions';
import { selectState } from 'redux/selectors';

const styles = theme => ({
  btnLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 400,
    [theme.breakpoints.down('md')]: {
      fontSize: 10,
    },
  },
  finalizeBtnInactive: {
    marginLeft: 0,
    marginRight: 0,
    padding: 0,
    minHeight: 'unset',
    flex: 1,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
    },
  },
  finalizeBtn: {
    backgroundColor: '#e25241',
    marginLeft: 0,
    marginRight: 0,
    padding: 0,
    minHeight: 'unset',
    flex: 1,
  },
});

class PayNowButton extends Component {
  static contextTypes = {
    currentUser: PropTypes.bool,
    payOnline: PropTypes.bool,
    isDistributor: PropTypes.bool,
  };

  static propTypes = {
    formatMessage: PropTypes.func.isRequired,
    info: PropTypes.shape({}).isRequired,
    orderRequesting: PropTypes.bool,
    classes: PropTypes.shape({}).isRequired,
    confirmDelivery: PropTypes.func.isRequired,
    refreshToken: PropTypes.func.isRequired,
  };

  static defaultProps = {
    orderRequesting: false,
  };

  state = { dialogOpen: false, payMethod: null };

  onSuccess = () => {
    this.closeDialog();
    this.props.refreshToken();
  };

  handleChangePayMethod = (e, payMethod) => this.setState({ payMethod });

  closeDialog = () => this.setState({ dialogOpen: false });

  render() {
    const { currentUser, payOnline } = this.context;
    const { formatMessage, info, orderRequesting, confirmDelivery, classes } = this.props;
    const { dialogOpen, payMethod } = this.state;

    const defaultPayMethod = get(currentUser, 'account.paymentDetail.default_source');
    const creditUsable = get(currentUser, 'account.creditUsable');
    let payMethods = (get(currentUser, 'account.paymentDetail.sources.data') || []).map(({ id, object, last4 }) => ({
      id,
      label: formatMessage(object === 'card' ? 'Card' : 'ACH'),
      subLabel: `*${last4}`,
      code: object === 'card' ? 'card' : 'ach',
      helper: object === 'card'
        ? (info.contact.payMode === 'ach' || info.contact.payMode === 'coin') && formatMessage('This vendor does not accept Card.')
        : (info.contact.payMode === 'card' || info.contact.payMode === 'coin') && formatMessage('This vendor does not accept ACH.'),
      disabled: ((info.contact.payMode === 'card' || info.contact.payMode === 'coin') && object !== 'card') || ((info.contact.payMode === 'ach' || info.contact.payMode === 'coin') && object === 'card'),
    }));
    payMethods.push({
      id: 'coin',
      label: formatMessage('Balance'),
      code: 'coin',
      disabled: creditUsable < info.totalPrice,
      helper: creditUsable < info.totalPrice && formatMessage('You do not have enough coins to place this order.'),
    });
    payMethods = payMethods.filter(m => m.code !== 'card');
    payMethods = orderBy(
      payMethods,
      [
        ({ disabled }) => disabled,
        ({ id }) => id === defaultPayMethod,
        ({ object }) => object === 'bank_account',
      ],
      ['asc', 'desc', 'desc'],
    );
    this.currentPayMethod = payMethod;
    if (!payMethod ||
      payMethods.find(({ id, disabled }) => disabled && id === this.currentPayMethod)
    ) {
      this.currentPayMethod = (payMethods.find(({ disabled }) => !disabled) || {}).id;
    }

    return (
      <div className="col-md-3 d-flex">
        <Tooltip tooltip={payOnline ? formatMessage('Pay Online') : formatMessage(payOnlineHelper)}>
          <Button
            classes={{
              label: classes.btnLabel,
              root: payOnline ? classes.finalizeBtn : classes.finalizeBtnInactive,
            }}
            className="confirm-button"
            disabled={info.mismatch || info.invalid || orderRequesting}
            onClick={payOnline ? () => this.setState({ dialogOpen: true }) : noop}
            variant="contained"
          >
            {formatMessage('Pay Now')}
          </Button>
        </Tooltip>
        {info.mismatch && <QuickHelp style={{ marginLeft: 5 }}>{formatMessage('Distributor did not confirm this order yet.')}</QuickHelp>}
        <Dialog open={dialogOpen} onClose={() => this.setState({ dialogOpen: false })}>
          <DialogContent>
            <h5>{formatMessage('Please select the payment method.')}</h5>
            <RadioGroup
              aria-label="Pay Method"
              name="pay-method"
              value={this.currentPayMethod}
              onChange={this.handleChangePayMethod}
            >
              {payMethods.map(({ id, label, subLabel, disabled, helper }) => (
                <FormControlLabel
                  value={id}
                  key={id}
                  control={<Radio className={classes.radio} />}
                  style={{ marginLeft: -5, marginBottom: 5 }}
                  disabled={disabled}
                  label={(
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{ width: 100 }}
                    >
                      <span style={{ fontWeight: payMethod === id ? 'bold' : 'normal' }}>
                        {label}
                        <span style={{ fontSize: 10, color: 'gray' }}> {subLabel}</span>
                      </span>
                      {helper && <QuickHelp>{helper}</QuickHelp>}
                    </div>
                  )}
                />
              ))}
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              onClick={() =>
                confirmDelivery(
                  this.currentPayMethod,
                  this.onSuccess,
                )
              }
              variant="contained"
              disabled={info.mismatch || info.invalid || orderRequesting}
            >
              {formatMessage('Pay Now')}
            </Button>
            <Button
              color="primary"
              onClick={this.closeDialog}
              variant="outlined"
              disabled={orderRequesting}
            >
              {formatMessage('Cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('orders', 'order')(state),
});

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
  refreshToken: () => dispatch(refreshToken()),
});

export default compose(
  setDisplayName('PayNowButton'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  immutableToJS,
)(PayNowButton);
